import { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Printer, ArrowLeft } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { formatINR, formatDate } from "../lib/format";
import { expandLineItem, expandLineItems, formatScopeChannels } from "../lib/proposalPackageDisplay";
import { resolveAmcFee, usesAnnualCost } from "../lib/lineItemFees";
import { DeliveryIcon } from "./proposal-builder-prototype/deliveryIcons";
import { DELIVERY_MODES } from "./proposal-builder-prototype/builderStorage";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

/* Iter 97.7 (2026-02-27): the print stylesheet was extracted to
   /app/frontend/src/lib/reedosPrintStyles.js so the Proposal Builder
   Prototype commercials preview can render in the same REEDOS visual
   language as the production proposal PDF without duplication.
   Production behaviour is unchanged — this is a pure CSS-string
   relocation. */
import { REEDOS_PRINT_STYLES as printStyles } from "../lib/reedosPrintStyles";
import { isHtmlContent, sanitizeNarrativeHtml } from "../lib/richText";

// Iter 67.4 — Document-level confidentiality notice. Sourced from the
// company.legal_name field so the entity is always accurate; falls back to
// the literal name if the company record predates `legal_name`.
// Per Rahul's 2026-05-28 brief: subtle and professional, not overly legal.
// Used on BOTH the cover footer (condensed visual) and the final-page
// doc-footer (full visual) — same prose, different surroundings.
const confidentialityNotice = (legalName) => {
    const entity = legalName || "REEDOS Software Solutions Pvt. Ltd.";
    return `This proposal is intended solely for the use of the recipient organization for evaluation and discussion purposes. The contents of this document are confidential and proprietary to ${entity} and shall not be reproduced, distributed or disclosed to any third party, in whole or in part, without prior written consent from ${entity}.`;
};

// Iter C.2.1.CAP — Print-safe capability label. Mirrors the curated soft-list
// for known codes; falls back to humanising an unknown free-text code.
const CAPABILITY_PRINT_LABELS = {
    web: "Web", android: "Android", ios: "iOS", api: "API",
    reporting: "Reporting", transaction_platform: "Transaction Platform",
    backoffice: "Backoffice", mis: "MIS", white_label: "White Label",
};
const capabilityLabel = (code) => CAPABILITY_PRINT_LABELS[code] || String(code || "")
    .split("_").map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(" ");


function resolvePlaceholders(text, ctx) {
    if (!text) return "";
    return text.replace(/\{\{([^}]+)\}\}/g, (_m, key) => {
        const v = ctx[key.trim()];
        return v == null ? `{{${key}}}` : String(v);
    });
}

// 2026-10-23 — T&C clause-body layout renderer. Splits the resolved body
// into per-line blocks so authored line breaks render exactly; lines opening
// a numbered sub-clause (2.1, 15.7 …) receive a hanging indent; a
// "For the Company" / "For REEDOS" pair (Clause 14 notices) renders as a
// two-column contact grid. Text content is never altered — only layout.
const SUBCLAUSE_LINE_RE = /^\s*\d+\.\d+(\.\d+)?[.)]?\s/;
function TncClauseBody({ text, ctx }) {
    const lines = String(resolvePlaceholders(text, ctx) || "").split("\n");
    const renderLines = (arr, kp) =>
        arr.map((l, i) => {
            const trimmed = l.trim();
            const cls = /^(For the Company\b|For REEDOS\b)/.test(trimmed)
                ? "clause-contact-head"
                : SUBCLAUSE_LINE_RE.test(l)
                    ? "clause-subline"
                    : "clause-line";
            return (
                <div key={`${kp}${i}`} className={cls}>
                    {trimmed === "" ? "\u00A0" : l}
                </div>
            );
        });
    const ci = lines.findIndex((l) => /^For the Company\b/.test(l.trim()));
    const ri = lines.findIndex((l) => /^For REEDOS\b/.test(l.trim()));
    if (ci !== -1 && ri !== -1 && ri > ci) {
        return (
            <>
                {ci > 0 && <div>{renderLines(lines.slice(0, ci), "pre")}</div>}
                <div className="tnc-contact-grid" data-testid="tnc-contact-grid">
                    <div className="tnc-contact-col">{renderLines(lines.slice(ci, ri), "co")}</div>
                    <div className="tnc-contact-col">{renderLines(lines.slice(ri), "re")}</div>
                </div>
            </>
        );
    }
    return <>{renderLines(lines, "ln")}</>;
}

function groupLinesByOption(lines) {
    const groups = new Map();
    for (const li of lines) {
        const key = (li.option_label && li.option_label.trim()) || "__default__";
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(li);
    }
    const hasOptions = [...groups.keys()].some((k) => k !== "__default__");
    return { groups, hasOptions };
}

export default function ProposalPrint() {
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    // 2026-07-22 · Proposal Versioning v2 — the print page honours a
    // `?version=<pvr_id>` query parameter so the PDF pipeline can render
    // any exact version snapshot. Backend /pdf appends this automatically
    // when a version is requested; direct browser access can also pass it.
    const requestedVersionId = searchParams.get("version");
    const [proposal, setProposal] = useState(null);
    const [company, setCompany] = useState(null);
    const [tnc, setTnc] = useState(null);
    const [loading, setLoading] = useState(true);
    // 2026-10-23 — live client record for dynamic {{client_contact_*}}
    // placeholders (the proposal's embedded snapshot may predate the
    // client's billing contact). Falls back silently to the snapshot.
    const [liveClient, setLiveClient] = useState(null);

    useEffect(() => {
        (async () => {
            try {
                const [pr, ci] = await Promise.all([
                    api.get(`/billing/proposal/${id}`),
                    api.get("/billing/company-info"),
                ]);
                setProposal(pr.data);
                setCompany(ci.data);
                const cid = pr.data?.client?.id || pr.data?.client_id;
                if (cid) {
                    try {
                        const cl = await api.get(`/billing/client/${cid}`);
                        setLiveClient(cl.data);
                    } catch {
                        setLiveClient(null); // snapshot fallback below
                    }
                }
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load proposal"));
            } finally {
                setLoading(false);
            }
        })();
    }, [id]);

    // Drive the default PDF filename via the page title.
    useEffect(() => {
        const prev = document.title;
        if (proposal?.proposal_number) {
            document.title = `REEDOS Proposal — ${proposal.proposal_number}`;
        }
        return () => { document.title = prev; };
    }, [proposal?.proposal_number]);

    const version = useMemo(() => {
        if (!proposal) return null;
        const versions = proposal.versions || [];
        // 2026-07-22 · Proposal Versioning v2 — resolve preference:
        //   1. explicit ?version=<pvr_id>  (highest priority — enables PDF
        //      snapshots and audit retrieval of any specific version),
        //   2. approved version (if any),
        //   3. latest ACTIVE (non-cancelled) version,
        //   4. absolute max version (last resort — proposal fully cancelled).
        if (requestedVersionId) {
            const requested = versions.find((v) => v.id === requestedVersionId);
            if (requested) return requested;
        }
        const approved = versions.find((v) => v.id === proposal.approved_version_id);
        if (approved) return approved;
        const active = versions.filter((v) => (v.status || "active") !== "cancelled");
        const pool = active.length ? active : versions;
        return pool.reduce((a, b) => (a.version_number > b.version_number ? a : b));
    }, [proposal, requestedVersionId]);

    // T&C reselection fix (2026-07-23) — resolve Full-mode clauses from the
    // selected version's `builder_state.tnc.fullTncId` so every version
    // binds to its own T&C snapshot on Save. Fall back to the proposal-level
    // `tnc_id` only when the resolved version has no stored `fullTncId`
    // (legacy versions authored before the Builder captured this marker).
    // Rekeying the fetch effect on `resolvedTncId` refreshes the clauses
    // whenever the selected version changes or the operator re-saves a new
    // version with a different T&C set.
    const resolvedTncId = useMemo(() => (
        version?.builder_state?.tnc?.fullTncId
        || proposal?.tnc_id
        || null
    ), [version, proposal?.tnc_id]);

    useEffect(() => {
        if (!proposal) return;
        (async () => {
            try {
                let tncResp;
                try {
                    tncResp = await api.get(
                        resolvedTncId
                            ? `/billing/proposal-tnc/${resolvedTncId}`
                            : "/billing/proposal-tnc",
                    );
                } catch {
                    tncResp = await api.get("/billing/proposal-tnc");
                }
                setTnc(tncResp.data);
            } catch (err) {
                toast.error(formatApiError(err, "Failed to load T&C"));
            }
        })();
    }, [proposal, resolvedTncId]);

    const versionIsCancelled = useMemo(
        () => version && (version.status || "active") === "cancelled",
        [version]
    );

    const totalsByGroup = useMemo(() => {
        if (!version) return { groups: new Map(), hasOptions: false, perGroupSubtotal: new Map(), grandSub: 0, grandAmc: 0, grandAnnual: 0 };
        const { groups, hasOptions } = groupLinesByOption(version.line_items);
        const perGroupSubtotal = new Map();
        let grandSub = 0, grandAmc = 0, grandAnnual = 0;
        for (const [label, lines] of groups.entries()) {
            let sub = 0, amc = 0, annual = 0;
            for (const dr of expandLineItems(lines)) {
                // Iter C.1.2b — Print totals derived from EXPANDED display
                // rows so the client-facing PDF rolls up package components
                // identically to the in-app proposal table. Atomic line
                // items continue to use the explicit-vs-legacy
                // resolveSoftwareFee/resolveAmcFee contract (encoded into
                // the expansion helper). Switching presentation mode
                // never changes the totals — mode-invariant by design.
                sub += dr.software_fee;
                amc += dr.amc_fee;
                annual += Number(dr.annual_cost_fee) || 0;
            }
            perGroupSubtotal.set(label, { sub, amc, annual });
            grandSub += sub;
            grandAmc += amc;
            grandAnnual += annual;
        }
        // 2026-10-23 — content-only presentation. The recurring column's
        // heading follows the product/service TYPE of the recurring charges,
        // never the rupee amounts: product_type "AMC"/"Package" = software
        // maintenance ("AMC Fee"); any other recurring charge (data/content
        // feeds, hosting, server-management subscriptions) = "Annual
        // Subscription Fee"; both kinds present = "AMC / Subscription Fee".
        // The setup column is hidden only for pure subscription proposals
        // with no one-time charges at all — software/mixed render unchanged.
        const allRows = [];
        for (const arr of groups.values()) allRows.push(...expandLineItems(arr));
        const anySetupAmount = allRows.some((dr) => Number(dr.software_fee) > 0);
        const anySetupComplimentary = (version.builder_state?.scope || [])
            .some((sr) => sr.setup_fee_pricing_mode === "complimentary");
        let anySubscription = false, anyMaintenance = false;
        const subFreqs = new Set();
        for (const li of version.line_items || []) {
            const recurring = resolveAmcFee(li) > 0
                || expandLineItem(li).some((dr) => Number(dr.amc_fee) > 0);
            if (!recurring) continue;
            const t = String(li.product_type || "").trim().toLowerCase();
            if (t === "amc" || t === "package") { anyMaintenance = true; continue; }
            anySubscription = true;
            const comps = Array.isArray(li.package_components) && li.package_components.length
                ? li.package_components.map((c) => c?.billing_frequency)
                : [li.billing_frequency];
            for (const f of comps) {
                const norm = String(f || "").trim().toLowerCase();
                if (norm && norm !== "one-time") subFreqs.add(norm);
            }
        }
        // A complimentary-AMC marker means the recurring column shows a
        // maintenance "Complimentary" cell — keep the AMC label accurate.
        if ((version.builder_state?.scope || []).some((sr) => sr.amc_pricing_mode === "complimentary")) {
            anyMaintenance = true;
        }
        // 2026-10-23 — frequency-aware subscription label: annual content/
        // data/hosting subscriptions → "Annual Subscription Fee"; monthly /
        // quarterly subscriptions get their own label; mixed periods fall
        // back to the generic "Subscription Fee".
        const subHeading = subFreqs.size === 1 && subFreqs.has("monthly") ? "Monthly Subscription Fee"
            : subFreqs.size === 1 && subFreqs.has("quarterly") ? "Quarterly Subscription Fee"
            : subFreqs.size === 1 && subFreqs.has("annual") ? "Annual Subscription Fee"
            : "Subscription Fee";
        const recurringHeading = anyMaintenance && anySubscription
            ? `AMC / ${subHeading}`
            : anySubscription ? subHeading : "AMC Fee";
        // Annual Cost (2026-10) — own column whenever any item carries it
        // (incl. Complimentary). The AMC column shows only when AMC is
        // explicitly included (priced, ₹0 Included or Complimentary).
        const anyAnnual = (version.line_items || []).some(usesAnnualCost);
        const amcExplicit = (li) => String(li.product_type || "").trim().toLowerCase() === "amc"
            || li.amc_pricing_mode === "complimentary"
            || (li.package_components || []).some((c) => c?.fee_kind === "amc");
        const showAmcCol = anyMaintenance || anySubscription || grandAmc > 0
            || (version.line_items || []).some(amcExplicit);
        const contentOnly = allRows.length > 0 && !anySetupAmount && !anySetupComplimentary
            && ((anySubscription && !anyMaintenance) || anyAnnual);
        return { groups, hasOptions, perGroupSubtotal, grandSub, grandAmc, grandAnnual, contentOnly, recurringHeading, anyAnnual, showAmcCol };
    }, [version]);

    const placeholderCtx = useMemo(() => {
        if (!proposal || !company) return {};
        return {
            client_name: proposal.client?.name || "",
            client_address: proposal.client?.address || "",
            client_legal_form: "",
            company_legal_name: company.legal_name || "",
            company_registered_office: company.registered_office || company.address || "",
            proposal_date: formatDate(proposal.created_date),
            // Owner directive 2026-09-24 — proposal validity tracks the
            // SELECTED VERSION's date, not the original proposal date.
            // {{proposal_date}} keeps meaning "original proposal date"
            // (used by the Agreement definition clause).
            version_date: formatDate(version?.created_date || proposal.created_date),
            notices_name: company.notices?.name || company.proposal_author?.name || "",
            notices_email: company.notices?.email || company.proposal_author?.email || "",
            notices_phone: company.notices?.phone || company.proposal_author?.phone || "",
            // 2026-10-23 — Client-side notice contact (Clause 14 "For the
            // Company"). Resolved from the LIVE client record's billing
            // contact (dynamic per proposal), falling back to the proposal's
            // embedded client snapshot, then kind_attention. Empty string
            // (never the raw placeholder) when no contact is on record.
            client_contact_name:
                liveClient?.billing_contact_name
                || proposal.client?.billing_contact_name
                || proposal.kind_attention || "",
            client_contact_email:
                liveClient?.billing_email
                || proposal.client?.billing_email || "",
        };
    }, [proposal, company, version, liveClient]);

    if (loading) return <div className="p-10 text-sm text-slate-500">Loading…</div>;
    if (!proposal || !version || !company || !tnc) return <div className="p-10">Proposal not found</div>;

    const { groups, hasOptions, perGroupSubtotal, grandSub, grandAmc, grandAnnual, contentOnly, recurringHeading, anyAnnual, showAmcCol } = totalsByGroup;
    const commercialCols = 2 + (contentOnly ? 0 : 1) + (showAmcCol ? 1 : 0) + (anyAnnual ? 1 : 0);

    // Iter C.2.1.e — Special Offer rendering inputs. The proposal payload
    // carries:
    //   • `special_offer` — the frozen snapshot (null if no offer applied).
    //   • `special_offer_summary` — the server-computed Standard / Discount /
    //     Net headline used by reports.
    // We re-derive the GST-on-Net + Grand Total here using the per-line
    // GST rates already aggregated (so multi-rate proposals render
    // correctly without forcing a server-side recomputation). The pre-tax
    // model is Section 15(3)(a) CGST — discount reduces taxable base
    // proportionally for each line. NOTE: this MUST stay below the early
    // `return null` guards above as plain `const`s — not hooks — to
    // preserve the hook-call count invariant across renders.
    const specialOffer = proposal?.special_offer || null;
    const offerSummary = proposal?.special_offer_summary || null;
    const showSpecialOffer = Boolean(specialOffer && specialOffer.enabled);
    const offerBlock = (() => {
        if (!showSpecialOffer) return null;
        const standard = Number(offerSummary?.standard ?? grandSub) || 0;
        const discount = Number(specialOffer?.discount_amount ?? 0);
        const net = Math.max(0, standard - discount);
        const discountFactor = standard > 0 ? (net / standard) : 0;
        let gstOnNet = 0;
        if (version) {
            for (const li of version.line_items || []) {
                const amt = Number(li.amount || 0);
                const pct = li.gst_applicable === false ? 0 : Number(li.gst_percent || 0);
                gstOnNet += amt * discountFactor * pct / 100;
            }
        }
        const grand = net + gstOnNet;
        return {
            standard: Math.round(standard * 100) / 100,
            discount: Math.round(discount * 100) / 100,
            net: Math.round(net * 100) / 100,
            gstOnNet: Math.round(gstOnNet * 100) / 100,
            grand: Math.round(grand * 100) / 100,
            label: specialOffer.label || "",
            discountType: specialOffer.discount_type,
            discountValue: specialOffer.discount_value,
        };
    })();

    // Iter 43.1 — Tax applicability for international proposals. Reads the
    // gst_applicable flag from the bill-to snapshot frozen onto the proposal
    // (Iter 43). Legacy proposals without the snapshot default to True so
    // existing domestic rendering is preserved.
    const gstApplicable = proposal?.bill_to?.gst_applicable !== false;
    // 2026-07-02 · V1 acceptance defect fix — the Commercial Summary GST
    // row must also respect the operator's Tax Display Mode. When the
    // Builder is materialised with tax_display.mode = "taxes_extra", every
    // line item is emitted with gst_applicable=false — the print previously
    // still showed "Plus: GST on Net Commercial Value +₹0" because the row
    // was gated only on the client-level bill_to.gst_applicable flag. This
    // second gate looks at the line-item flags so the row is hidden when
    // the operator has opted out of the explicit GST display. International
    // clients (bill_to.gst_applicable=false) behaviour is unchanged.
    const anyLineHasGst = (version?.line_items || []).some(
        (li) => li.gst_applicable !== false && Number(li.gst_percent || 0) > 0
    );
    const showGstRow = gstApplicable && anyLineHasGst;
    const totalSuffix = showGstRow ? " (excl. GST)" : "";
    const figuresFootnote = gstApplicable
        ? "All figures in INR. GST extra at applicable rates."
        : "All figures in INR. Export billing — no GST applicable.";

    const renderGroupLabel = (label) =>
        label === "__default__" ? (hasOptions ? "Other items" : null) : label;

    // Iter 66.14 — cap option labels in display surfaces so a long
    // commercial paragraph doesn't render as a banner. The full text
    // is intended for `option_description` (rendered ONCE under the
    // group band, italic) instead of as a duplicated section header.
    const OPTION_LABEL_DISPLAY_LIMIT = 80;
    const truncateLabel = (s, limit = OPTION_LABEL_DISPLAY_LIMIT) => {
        if (!s) return s;
        const clean = String(s).trim();
        if (clean.length <= limit) return clean;
        return clean.slice(0, limit - 1).trimEnd() + "…";
    };

    const sectionEyebrow = (num, label) => (
        <div className="eyebrow"><span className="num">{num}</span>{label}</div>
    );

    return (
        <div className="min-h-screen bg-slate-100" data-testid="proposal-print-page" id="proposal-print-root">
            <style>{printStyles}</style>

            {/* On-screen toolbar — hidden in print */}
            <div className="no-print bg-white border-b border-slate-200 px-6 py-3 sticky top-0 z-10 flex items-center justify-between">
                <button onClick={() => window.history.back()} className="text-sm text-slate-700 hover:text-slate-900 inline-flex items-center gap-1.5">
                    <ArrowLeft size={14} /> Back
                </button>
                <Button onClick={() => window.print()} data-testid="print-proposal-btn" className="bg-slate-900 hover:bg-slate-800 text-white gap-1.5">
                    <Printer size={16} weight="fill" /> Print / Save as PDF
                </Button>
            </div>

            <div className="print-shell shadow-sm">
                {/* ── Top header bar ─────────────────────────────── */}
                <div className="print-header">
                    <img src="/reedos-logo.png" alt="REEDOS" className="logo" data-testid="print-logo" />
                    <div className="doc-meta">
                        <div className="tag">Confidential</div>
                        <div className="num"><strong>{proposal.proposal_number}</strong></div>
                        {/* Owner directive 2026-09-24 — the header always shows the
                            ORIGINAL proposal date; the meta-card Date row carries the
                            selected version's date. */}
                        <div data-testid="original-proposal-date">Original Proposal Date · {formatDate(proposal.created_date)}</div>
                    </div>
                </div>

                {/* ── Cover page ─────────────────────────────────── */}
                <section className="cover" data-testid="proposal-cover">
                    {/* 2026-07-22 · Proposal Versioning v2 — cancelled version watermark.
                        Rendered for historical retrieval / audit only. The mark is fixed
                        to the top-right of the cover so it does NOT obscure the client's
                        name or key metadata but is impossible to miss at a glance. */}
                    {versionIsCancelled && (
                        <div
                            data-testid="proposal-cancelled-watermark"
                            style={{
                                position: "absolute",
                                top: "18mm",
                                right: "-14mm",
                                transform: "rotate(20deg)",
                                border: "3px solid #b91c1c",
                                color: "#b91c1c",
                                fontFamily: "sans-serif",
                                fontWeight: 800,
                                fontSize: "18pt",
                                letterSpacing: "0.14em",
                                padding: "6px 40px",
                                background: "rgba(255,255,255,0.85)",
                                textTransform: "uppercase",
                                zIndex: 30,
                            }}
                        >
                            Cancelled · for reference only
                        </div>
                    )}
                    <div className="cover-hero">
                        <div className="cover-tag">
                            <span>Business Proposal</span>
                            <span className="pipe" />
                            <span>FY {(() => { const d = new Date(proposal.created_date); const y = d.getFullYear(); const fy = d.getMonth() >= 3 ? y : y - 1; return `${String(fy).slice(2)}–${String(fy + 1).slice(2)}`; })()}</span>
                        </div>
                        <div className="cover-rule" />
                        <div className="cover-prepared">Prepared exclusively for</div>
                        <h1 className="cover-client" data-testid="proposal-cover-client-name">{proposal.client?.name || "Client name not set"}</h1>

                        <div className="meta-card">
                            <div className="row">
                                <div className="label">Proposal No.</div>
                                <div className="value"><span className="mono">{proposal.proposal_number}</span></div>
                            </div>
                            <div className="row">
                                <div className="label">Version Date</div>
                                {/* Owner directive 2026-09-24 — this row shows the
                                    SELECTED VERSION's date; the original proposal
                                    date stays at the top-right header. */}
                                <div className="value" data-testid="proposal-version-date">{formatDate(version.created_date || proposal.created_date)}</div>
                            </div>
                            {proposal.kind_attention && (
                                <div className="row">
                                    <div className="label">Kind Attention</div>
                                    <div className="value">{proposal.kind_attention}</div>
                                </div>
                            )}
                            <div className="row">
                                <div className="label">Address</div>
                                <div className="value"><span className="soft">{proposal.client?.address || "—"}</span></div>
                            </div>
                            <div className="row">
                                <div className="label">Status</div>
                                <div className="value">{proposal.status} <span className="soft">·</span> Version {version.version_number}</div>
                            </div>
                            <div className="row span">
                                <div className="label">Submitted By</div>
                                <div className="value">
                                    {/* Iter 66.11 — prefer the proposal's audit-stable
                                        `proposal_author` snapshot (captured at create
                                        time from the rep's profile); fall back to the
                                        env-configured signatory only when the proposal
                                        predates the snapshot field. */}
                                    {(() => {
                                        const a = proposal.proposal_author || company.proposal_author || {};
                                        const contact = [a.phone, a.email].filter(Boolean).join("  ·  ");
                                        return (
                                            <>
                                                <span className="submitter-name">{a.name || "—"}</span>
                                                {contact && <span className="submitter-contact">{contact}</span>}
                                            </>
                                        );
                                    })()}
                                </div>
                            </div>
                            {proposal.bill_to_locked_at && proposal.bill_to?.snapshot_at && (
                                <div className="row" data-testid="bill-to-locked-stamp">
                                    <div className="label">Bill-to fixed</div>
                                    <div className="value">
                                        <span className="locked-pill">As on {formatDate(proposal.bill_to.snapshot_at)}</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Iter 67.4 — Cover-level confidentiality notice. Sub-paragraph
                        at the base of the cover, sized smaller than the meta-card.
                        Keeps the legal stance visible from page one without dominating
                        the visual hierarchy. Full version repeats on the final page. */}
                    <div className="cover-confidentiality" data-testid="proposal-cover-confidentiality">
                        <span className="conf-eyebrow">Confidential &amp; Proprietary</span>
                        {confidentialityNotice(company.legal_name)}
                    </div>
                </section>

                {/* ── 01 / Scope ─────────────────────────────────── */}
                {version.header_text && (
                    <section className="section page-break-before" data-testid="proposal-print-scope">
                        <div className="section-title">
                            {sectionEyebrow("01", "Scope of the Project")}
                            <h2>Scope of the Project</h2>
                            <div className="accent-rule" />
                        </div>
                        {isHtmlContent(version.header_text) ? (
                            <div
                                className="scope-body scope-body-rich"
                                // Sanitized on both write (backend
                                // `html_sanitizer`) and read (DOMPurify via
                                // `sanitizeNarrativeHtml`).
                                dangerouslySetInnerHTML={{ __html: sanitizeNarrativeHtml(version.header_text) || "" }}
                            />
                        ) : (
                            <div className="scope-body">{version.header_text}</div>
                        )}
                    </section>
                )}

                {/* ── 02 / Commercials ───────────────────────────── */}
                <section className="section" data-testid="proposal-print-commercials-section">
                    <div className="section-title">
                        {sectionEyebrow("02", hasOptions ? "Commercials · Multiple Options" : "Commercials")}
                        <h2>Commercials</h2>
                        <div className="accent-rule" />
                    </div>
                    <div className="commercials-card">
                        <table className="commercials" data-testid="proposal-print-commercials">
                            <thead>
                                <tr>
                                    <th className="text-left num-col">Sr.</th>
                                    <th className="text-left">Product</th>
                                    {/* 2026-10-23 — content-only proposals hide the unused
                                        setup column entirely; software/mixed keep it. */}
                                    {!contentOnly && (
                                        <th className="text-right" style={{ width: "36mm" }}>Software &amp; Set-up Fee</th>
                                    )}
                                    {showAmcCol && <th className="text-right" style={{ width: "32mm" }} data-testid="print-amc-heading">{recurringHeading}</th>}
                                    {anyAnnual && <th className="text-right" style={{ width: "30mm" }} data-testid="print-annual-cost-heading">Annual Cost</th>}
                                </tr>
                            </thead>
                            <tbody>
                                {(() => {
                                    const rows = [];
                                    let counter = 0;
                                    // Iter 97.15 — Unified 3-mode rendering for Builder-materialised
                                    // proposals (operator directive 2026-02-27). The Print/PDF applies
                                    // identical price-visibility rules to the Builder Preview:
                                    //   - Itemized     → all prices visible · subtotals visible
                                    //   - Grouped      → module-row prices BLANK ("") · subtotals visible
                                    //   - Consolidated → all row prices BLANK ("") · subtotals hidden
                                    // Blank cells are empty strings — no dash, no zero.
                                    const allLineItems = [];
                                    for (const arr of groups.values()) allLineItems.push(...arr);
                                    const builderMode = (allLineItems.find((li) => li.presentation_mode) || {}).presentation_mode;
                                    const builderShowModulePrices = builderMode === "itemized";
                                    const builderShowAtomicPrices = builderMode !== "consolidated";
                                    const builderShowSubtotals = builderMode && builderMode !== "consolidated";
                                    const formatBuilderCell = (val, visible) => {
                                        if (!visible) return "";
                                        return val > 0 ? formatINR(val, proposal?.currency) : "";
                                    };
                                    // Complimentary marker (2026-07-23) — same lookup pattern
                                    // as ProposalDetail: cross-reference the current version's
                                    // builder_state.scope[*] by product label. Legacy versions
                                    // without builder_state produce an empty map → render path
                                    // is byte-identical to pre-hotfix behaviour.
                                    const scopeMarkersByLabel = new Map();
                                    for (const sr of (version.builder_state?.scope || [])) {
                                        const label = sr.moduleLabel || sr.productName;
                                        if (!label) continue;
                                        scopeMarkersByLabel.set(label, {
                                            setup: sr.setup_fee_pricing_mode === "complimentary",
                                            amc: sr.amc_pricing_mode === "complimentary",
                                        });
                                    }
                                    for (const [label, lines] of groups.entries()) {
                                        if (hasOptions) {
                                            rows.push(
                                                <tr key={`band-${label}`} className="option-band">
                                                    <td colSpan={commercialCols} data-testid={`option-band-${label}`} title={label === "__default__" ? "" : label}>
                                                        Product · {renderGroupLabel(label) ? truncateLabel(renderGroupLabel(label)) : ""}
                                                    </td>
                                                </tr>
                                            );
                                            // Iter 66.14 — render the shared option_description (if any
                                            // line in the group carries one) ONCE under the band.
                                            const optionDescription = (lines.find((x) => (x.option_description || "").trim()) || {}).option_description;
                                            if (optionDescription) {
                                                rows.push(
                                                    <tr key={`band-desc-${label}`} className="option-band-desc">
                                                        <td colSpan={commercialCols} className="option-description" data-testid={`option-band-desc-${label}`}>
                                                            {optionDescription}
                                                        </td>
                                                    </tr>
                                                );
                                            }
                                            counter = 0;
                                        }
                                        for (const li of lines) {
                                            // Iter C.1.2b — Expand each line item into 1..N
                                            // display rows. Atomic lines emit 1 row (1:1 with
                                            // pre-C.1.2b behaviour). Package lines emit N
                                            // component rows in itemized mode, 1 row labelled
                                            // `package_consolidated_label` in consolidated mode,
                                            // or visible-rows + 1 "Bundled services" rollup in
                                            // hybrid mode. The PDF deliberately does NOT show
                                            // a "Package" banner row — clients see the
                                            // commercial structure as the operator chose to
                                            // present it. Internal head is screen-only context.
                                            for (const dr of expandLineItem(li)) {
                                                counter += 1;
                                                // Iter 97.15 — Per-mode price visibility for Builder rows.
                                                const isBuilderRow = !!dr.presentation_mode;
                                                const builderRowKind = dr.row_kind;
                                                const showSetupCell = !isBuilderRow
                                                    || (builderRowKind === "module" ? builderShowModulePrices
                                                        : builderRowKind === "atomic" ? builderShowAtomicPrices
                                                            : false);
                                                const setupCellContent = isBuilderRow
                                                    ? formatBuilderCell(dr.software_fee, showSetupCell)
                                                    : (dr.software_fee > 0 ? formatINR(dr.software_fee, proposal?.currency) : "—");
                                                const amcCellContent = isBuilderRow
                                                    ? formatBuilderCell(dr.amc_fee, showSetupCell)
                                                    : (dr.amc_fee > 0 ? formatINR(dr.amc_fee, proposal?.currency) : "—");
                                                // Complimentary marker override (2026-07-23) —
                                                // Source-of-truth is the presence of the marker
                                                // on builder_state.scope[*]. Do NOT gate on
                                                // isBuilderRow (which requires per-line
                                                // presentation_mode); the map is empty for legacy
                                                // versions so this branch is naturally a no-op.
                                                const isProductHeader = builderRowKind === "product_header";
                                                const dispMarkers = !isProductHeader
                                                    ? scopeMarkersByLabel.get(dr.product_name)
                                                    : null;
                                                const finalSetupCellContent = (dispMarkers?.setup)
                                                    ? <span className="complimentary-cell">Complimentary</span>
                                                    : setupCellContent;
                                                const finalAmcCellContent = (dispMarkers?.amc)
                                                    ? <span className="complimentary-cell">Complimentary</span>
                                                    : amcCellContent;
                                                const annualVal = Number(dr.annual_cost_fee) || 0;
                                                const annualCellContent = dr.annual_cost_complimentary
                                                    ? <span className="complimentary-cell">Complimentary</span>
                                                    : isBuilderRow ? formatBuilderCell(annualVal, showSetupCell)
                                                        : (annualVal > 0 ? formatINR(annualVal, proposal?.currency) : "—");
                                                const rowClass = builderRowKind === "module"
                                                    ? "itemized-module-row"
                                                    : builderRowKind === "product_header"
                                                        ? "itemized-product-header"
                                                        : "";
                                                const nameContent = builderRowKind === "module"
                                                    ? (<div className="scope-module-line">↳ {dr.product_name}</div>)
                                                    : (<><div className="product-name">{dr.product_name}</div>{dr.product_subtitle && (<div className="product-sub whitespace-pre-line">{dr.product_subtitle}</div>)}{Array.isArray(dr.delivery_modes) && dr.delivery_modes.length > 0 && (<div className="capability-icons">{dr.delivery_modes.map((c) => { const dm = DELIVERY_MODES.find((x) => x.code === c); if (!dm) return null; return <DeliveryIcon key={c} iconName={dm.iconName} label={dm.label} size={13} />; })}</div>)}</>);
                                                rows.push(
                                                    <tr key={dr.id} className={rowClass}>
                                                        <td className="num-col">{builderRowKind === "module" ? "" : String(counter).padStart(2, "0")}</td>
                                                        <td>
                                                            {nameContent}
                                                            {/* Iter C.2.1.CAP — capability badges in print. Print-safe text pills (no icon-font dependency). */}
                                                            {!isBuilderRow && Array.isArray(li?.capabilities) && li.capabilities.length > 0 && (
                                                                <div className="capability-strip">
                                                                    {li.capabilities.map((code) => (
                                                                        <span key={code} className="capability-pill">{capabilityLabel(code)}</span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                            {Array.isArray(dr.scope_items) && dr.scope_items.length > 0 && (
                                                                <div className="scope-block">
                                                                    <div className="scope-label">Includes</div>
                                                                    <ul className="scope-list">
                                                                        {dr.scope_items.map((s, i) => (
                                                                            <li key={i}>
                                                                                <span className="scope-module">{s.module_label}</span>
                                                                                {s.channels && (
                                                                                    <span className="scope-channels"> ({formatScopeChannels(s.channels)})</span>
                                                                                )}
                                                                            </li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                        </td>
                                                        {!contentOnly && <td className="figure">{finalSetupCellContent}</td>}
                                                        {showAmcCol && <td className="figure">{finalAmcCellContent}</td>}
                                                        {anyAnnual && <td className="figure" data-testid={`print-annual-cost-${dr.id}`}>{annualCellContent}</td>}
                                                    </tr>
                                                );
                                            }
                                        }
                                        if (hasOptions) {
                                            // For Builder rows, only emit the band subtotal in
                                            // Itemized + Grouped modes (operator spec). For legacy
                                            // (non-Builder) proposals continue to emit the subtotal
                                            // unconditionally — backward-compat.
                                            const emitSub = !builderMode || builderShowSubtotals;
                                            if (emitSub) {
                                                const gs = perGroupSubtotal.get(label) || { sub: 0, amc: 0, annual: 0 };
                                                rows.push(
                                                    <tr key={`sub-${label}`} className="option-subtotal">
                                                        <td colSpan={2} style={{ textAlign: "right" }}>Subtotal — {truncateLabel(renderGroupLabel(label), 40)}{totalSuffix}</td>
                                                        {!contentOnly && <td className="figure figure-em">{gs.sub > 0 ? formatINR(gs.sub, proposal?.currency) : ""}</td>}
                                                        {showAmcCol && <td className="figure figure-em">{gs.amc ? formatINR(gs.amc, proposal?.currency) : ""}</td>}
                                                        {anyAnnual && <td className="figure figure-em">{gs.annual ? formatINR(gs.annual, proposal?.currency) : ""}</td>}
                                                    </tr>
                                                );
                                            }
                                        }
                                    }
                                    return rows;
                                })()}
                                {!hasOptions && (
                                    <tr className="totals-row">
                                        <td colSpan={2} className="label">Total{totalSuffix}</td>
                                        {!contentOnly && <td className="figure figure-em">{formatINR(grandSub, proposal?.currency)}</td>}
                                        {showAmcCol && <td className="figure figure-em">{grandAmc ? formatINR(grandAmc, proposal?.currency) : "—"}</td>}
                                        {anyAnnual && <td className="figure figure-em" data-testid="print-grand-annual-cost">{grandAnnual ? formatINR(grandAnnual, proposal?.currency) : "—"}</td>}
                                    </tr>
                                )}
                                {hasOptions && (
                                    <tr className="grand-total-row">
                                        <td colSpan={2} className="label">All Options · Grand Total{totalSuffix}</td>
                                        {!contentOnly && <td className="figure figure-em">{formatINR(grandSub, proposal?.currency)}</td>}
                                        {showAmcCol && <td className="figure figure-em">{grandAmc ? formatINR(grandAmc, proposal?.currency) : "—"}</td>}
                                        {anyAnnual && <td className="figure figure-em" data-testid="print-grand-annual-cost">{grandAnnual ? formatINR(grandAnnual, proposal?.currency) : "—"}</td>}
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="commercials-footnote">{figuresFootnote}</div>

                    {/* Iter 97.16 — Delivery-mode legend mirroring the
                        Builder Preview. Enumerates only the modes actually
                        used across the proposal's Builder-materialised
                        lines. Hidden when no Builder lines carry
                        delivery_modes (e.g. legacy proposals). */}
                    {(() => {
                        const used = new Set();
                        for (const li of version?.line_items || []) {
                            if (!li.presentation_mode) continue;
                            for (const c of (li.delivery_modes || [])) used.add(c);
                        }
                        if (used.size === 0) return null;
                        const visible = DELIVERY_MODES.filter((d) => used.has(d.code));
                        return (
                            <div className="commercials-legend" data-testid="proposal-print-delivery-legend">
                                <div className="commercials-legend-title">Delivery Modes</div>
                                <div className="commercials-legend-items">
                                    {visible.map((dm) => (
                                        <span
                                            key={dm.code}
                                            className="commercials-legend-item"
                                            data-testid={`proposal-print-delivery-legend-${dm.code}`}
                                        >
                                            <DeliveryIcon iconName={dm.iconName} label={dm.label} size={14} />
                                            <span>{dm.label}</span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Iter C.2.1.e — Special Offer commercial-summary block.
                        Renders ONLY when an offer is applied. Internal
                        reason_code is intentionally NOT rendered here. */}
                    {showSpecialOffer && offerBlock && (
                        <div
                            className="commercial-summary"
                            data-testid="proposal-print-special-offer"
                        >
                            <div className="cs-eyebrow">Commercial Summary</div>
                            <div className="cs-row">
                                <span>Standard Commercial Value</span>
                                <span>{formatINR(offerBlock.standard, proposal?.currency)}</span>
                            </div>
                            <div className="cs-row cs-less">
                                <span>
                                    Less: {offerBlock.label}
                                    {offerBlock.discountType === "Percentage" && (
                                        <span className="cs-pct">({offerBlock.discountValue}%)</span>
                                    )}
                                </span>
                                <span>−{formatINR(offerBlock.discount, proposal?.currency)}</span>
                            </div>
                            {/* UAT 45.6.6 (2026-07-14) — customer-facing description
                                for the standardized offer taxonomy. Renders under the
                                Less row when present. Editable by the operator in the
                                Commercial Builder; auto-populated from the offer_type
                                catalog otherwise. */}
                            {specialOffer?.description && (
                                <div
                                    className="cs-description"
                                    data-testid="proposal-print-special-offer-description"
                                >
                                    {specialOffer.description}
                                </div>
                            )}
                            <div className="cs-row cs-net">
                                <span>Net Commercial Value</span>
                                <span>{formatINR(offerBlock.net, proposal?.currency)}</span>
                            </div>
                            {showGstRow && (
                                <div className="cs-row cs-tax">
                                    <span>Plus: GST on Net Commercial Value</span>
                                    <span>+{formatINR(offerBlock.gstOnNet, proposal?.currency)}</span>
                                </div>
                            )}
                            <div className="cs-row cs-grand">
                                <span>Grand Total</span>
                                <span>{formatINR(offerBlock.grand, proposal?.currency)}</span>
                            </div>
                            <div className="cs-footnote">
                                Special offer issued {formatDate(specialOffer?.applied_at)} — valid for this proposal only.
                            </div>
                        </div>
                    )}
                </section>

                {/* ── 03 / Payment Terms ─────────────────────────── */}
                {Array.isArray(tnc.payment_terms) && tnc.payment_terms.length > 0 && (
                    <section className="section keep-together" data-testid="proposal-print-payment-terms-section">
                        <div className="section-title">
                            {sectionEyebrow("03", "Payment Terms & Other Conditions")}
                            <h2>Payment Terms &amp; Other Conditions</h2>
                            <div className="accent-rule" />
                        </div>
                        <ol className="terms-list" data-testid="proposal-print-payment-terms">
                            {tnc.payment_terms.map((t, i) => {
                                // 2026-10-23 — imported terms may carry their own "1."
                                // prefixes; drop a leading number only when it matches the
                                // list position so the <ol> marker stays the single source
                                // of numbering. Text otherwise rendered verbatim.
                                const txt = resolvePlaceholders(t, placeholderCtx);
                                const deduped = txt.replace(new RegExp(`^\\s*${i + 1}[.)]\\s+`), "");
                                return <li key={i}>{deduped}</li>;
                            })}
                        </ol>
                    </section>
                )}

                {/* ── 04 / Terms & Conditions ──────────────────────
                    PB-004 (V1-4.T&C) — 3-way rendering branch driven by
                    `proposal.tnc_mode`. Legacy proposals (missing field)
                    default to "full" — byte-identical to pre-PB-004 output. */}
                {(() => {
                    const tncMode = proposal.tnc_mode || "full";
                    const refNumber = proposal.tnc_reference_proposal_number || "";
                    const customNote = proposal.tnc_reference_note || "";
                    const defaultRefStatement = refNumber
                        ? `The Terms & Conditions contained in ${refNumber} shall continue to apply unless specifically modified in this proposal.`
                        : "The Terms & Conditions contained in the reference proposal shall continue to apply unless specifically modified in this proposal.";
                    const refStatement = (customNote && customNote.trim()) || defaultRefStatement;
                    const additionalClauses = Array.isArray(proposal.tnc_additional_clauses) ? proposal.tnc_additional_clauses : [];

                    if (tncMode === "reference") {
                        return (
                            <section className="section page-break-before" data-testid="proposal-print-tnc" data-tnc-mode="reference">
                                <div className="section-title">
                                    {sectionEyebrow("04", "Terms & Conditions")}
                                    <h2>Terms &amp; Conditions</h2>
                                    <div className="accent-rule" />
                                </div>
                                <div className="tnc-reference-statement" data-testid="tnc-reference-statement">
                                    {refStatement}
                                </div>
                            </section>
                        );
                    }

                    if (tncMode === "reference_plus_additional") {
                        return (
                            <section className="section page-break-before" data-testid="proposal-print-tnc" data-tnc-mode="reference_plus_additional">
                                <div className="section-title">
                                    {sectionEyebrow("04", "Terms & Conditions")}
                                    <h2>Terms &amp; Conditions</h2>
                                    <div className="accent-rule" />
                                </div>
                                <div className="tnc-reference-statement" data-testid="tnc-reference-statement">
                                    {refStatement}
                                </div>
                                {additionalClauses.length > 0 && (
                                    <div className="tnc-additional-clauses-block" data-testid="tnc-additional-clauses-block">
                                        <h3 className="tnc-subheading" data-testid="tnc-additional-clauses-heading">Additional Clauses</h3>
                                        <div className="tnc-clauses">
                                            {additionalClauses.map((c, idx) => (
                                                <div key={idx} className="clause" data-testid={`tnc-print-additional-clause-${idx + 1}`}>
                                                    <div className="clause-title"><span className="num">{idx + 1}.</span>{c.title || ""}</div>
                                                    {c.body && (
                                                        <div className="clause-body">{c.body}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </section>
                        );
                    }

                    // Default: mode === "full" — legacy behaviour, master T&C set
                    return (
                        <section className="section page-break-before" data-testid="proposal-print-tnc" data-tnc-mode="full">
                            <div className="section-title">
                                {sectionEyebrow("04", "Terms & Conditions")}
                                <h2>Terms &amp; Conditions</h2>
                                <div className="accent-rule" />
                            </div>
                            {tnc.preamble && (
                                <div className="tnc-preamble">{resolvePlaceholders(tnc.preamble, placeholderCtx)}</div>
                            )}
                            <div className="tnc-clauses">
                                {tnc.clauses.map((c) => (
                                    <div key={c.order} className="clause" data-testid={`tnc-print-clause-${c.order}`}>
                                        {/* 2026-10-23 — suppress the auto number when the
                                            title already carries it (imported sets keep
                                            "1 Agreement"-style numbering) so headings never
                                            print "1. 1 Agreement". Wording untouched. */}
                                        <div className="clause-title">
                                            {new RegExp(`^${c.order}[.\\s)]`).test(String(c.title || "").trim()) ? null : (
                                                <span className="num">{c.order}.</span>
                                            )}
                                            {c.title}
                                        </div>
                                        <div className="clause-body"><TncClauseBody text={c.body} ctx={placeholderCtx} /></div>
                                    </div>
                                ))}
                            </div>
                        </section>
                    );
                })()}

                {/* 2026-10-23 — keep signatures + document footer together so the
                    confidentiality/footer block never spills alone onto a final page.
                    If they don't fit after the last clause, the whole closing unit
                    moves as one (a proper signature page), never a lone footer. */}
                <div className="doc-end" data-testid="proposal-print-doc-end">
                {/* ── Signatures ─────────────────────────────────── */}
                <section className="signature-grid" data-testid="proposal-print-signatures">
                    <div className="signature-block">
                        <div className="for-label">For (Client)</div>
                        <div className="for-name">{proposal.client?.name}</div>
                        <div className="sign-line" />
                        <div className="sign-meta">
                            <div>Authorised Signatory</div>
                            <div style={{ textAlign: "right" }}>Date</div>
                        </div>
                    </div>
                    <div className="signature-block">
                        <div className="for-label">For (REEDOS)</div>
                        <div className="for-name">{company.legal_name}</div>
                        <div className="sign-line" />
                        <div className="sign-meta">
                            <div>{company.signatory_designation || "Authorised Signatory"}</div>
                            <div style={{ textAlign: "right" }}>Date</div>
                        </div>
                    </div>
                </section>

                {/* ── Document footer ────────────────────────────── */}
                <div className="doc-footer">
                    {/* Iter 67.4 — Formal proposal-level confidentiality notice.
                        Sits above the existing brand/legal row so the recipient's
                        last view of the document carries the legal stance prominently
                        without dominating the visual hierarchy of the body. */}
                    <div className="confidentiality-block" data-testid="proposal-footer-confidentiality">
                        <span className="conf-eyebrow">Confidential &amp; Proprietary</span>
                        {confidentialityNotice(company.legal_name)}
                    </div>
                    <div className="footer-row">
                        <div className="org-line"><strong>{company.legal_name}</strong> · GSTIN {company.gstin}</div>
                        <div className="conf">REEDOS · Confidential</div>
                    </div>
                </div>
                </div>{/* /doc-end */}
            </div>
        </div>
    );
}
